import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { dictionaryService } from '../services/dictionaryService';
import { learningService } from '../services/learningService';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { SIGHT_WORD_LEVELS } from '../constants/sightWords';
import './SightWordsReferencePage.css';

type MasteryStatus = 'mastered' | 'developing' | 'struggling' | 'unseen';

interface MasteryEntry {
    correctCount: number;
    errorCount: number;
    lastSeenAt: string | null;
}

function getMasteryStatus(entry?: MasteryEntry): MasteryStatus {
    if (!entry) return 'unseen';
    const total = entry.correctCount + entry.errorCount;
    if (total === 0) return 'unseen';
    const accuracy = entry.correctCount / total;
    if (accuracy >= 0.8 && total >= 3) return 'mastered';
    if (accuracy < 0.4 && total >= 2) return 'struggling';
    return 'developing';
}

/** Resolve audio URL from dictionary API */
function resolveAudioUrl(url: string): string {
    if (!url) return url;
    if (/\/(localhost|127\.0\.0\.1):\d+/.test(url)) {
        const path = url.replace(/^https?:\/\/(localhost|127\.0\.0\.1):\d+/, '');
        return `${API_BASE_URL}${path}`.replace(/ /g, '%20');
    }
    if (url.startsWith('/')) {
        return `${API_BASE_URL}${url}`.replace(/ /g, '%20');
    }
    return url.replace(/ /g, '%20');
}

// ─── Word card with audio + mastery color ───

const WordCard: React.FC<{
    word: string;
    status: MasteryStatus;
    speakingWord: string | null;
    loadingWord: string | null;
    onSpeak: (word: string) => void;
}> = ({ word, status, speakingWord, loadingWord, onSpeak }) => {
    const isPlaying = speakingWord === word;
    const isLoading = loadingWord === word;

    return (
        <button
            className={`sight-words__card sight-words__card--${status} ${isPlaying ? 'sight-words__card--playing' : ''} ${isLoading ? 'sight-words__card--loading' : ''}`}
            onClick={() => onSpeak(word)}
            disabled={isLoading}
            type="button"
        >
            <span className="sight-words__word">{word}</span>
            {isLoading ? (
                <svg className="sight-words__spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
            ) : (
                <svg className="sight-words__speaker" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
            )}
        </button>
    );
};

// ─── Main page ───

const SightWordsReferencePage: React.FC = () => {
    const { t } = useTranslation('sightWordsRef');
    const { user } = useAuth();
    const location = useLocation();
    const [speakingWord, setSpeakingWord] = useState<string | null>(null);
    const [loadingWord, setLoadingWord] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [masteryMap, setMasteryMap] = useState<Record<string, MasteryEntry>>({});

    // Fetch mastery data
    useEffect(() => {
        if (!user) return;
        learningService.getSightWordMastery()
            .then(setMasteryMap)
            .catch(() => { /* silent */ });
    }, [user]);

    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }
        }
    }, [location.hash]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // Compute per-level mastery counts
    const levelStats = useMemo(() => {
        return SIGHT_WORD_LEVELS.map(({ level, words }) => {
            let mastered = 0;
            for (const w of words) {
                if (getMasteryStatus(masteryMap[w.toLowerCase()]) === 'mastered') mastered++;
            }
            return { level, mastered, total: words.length };
        });
    }, [masteryMap]);

    const handleSpeak = useCallback(async (word: string) => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setSpeakingWord(null);
        setLoadingWord(word);

        try {
            const result = await dictionaryService.lookup(word, { generateAudio: true });
            const audioUrl = result.audio?.[0]?.url;
            if (!audioUrl) {
                setLoadingWord(null);
                return;
            }

            const audio = new Audio(resolveAudioUrl(audioUrl));
            audioRef.current = audio;
            setLoadingWord(null);
            setSpeakingWord(word);

            audio.onended = () => setSpeakingWord(null);
            audio.onerror = () => setSpeakingWord(null);
            await audio.play();
        } catch {
            setLoadingWord(null);
            setSpeakingWord(null);
        }
    }, []);

    const hasMastery = Object.keys(masteryMap).length > 0;

    return (
        <div className="sight-words">
            <div className="sight-words__header">
                <Link to="/knowledge-graph" className="sight-words__back">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
                    </svg>
                    {t('backToGraph', 'Knowledge Graph')}
                </Link>
                <h1 className="sight-words__title">{t('title', 'Sight Words')}</h1>
                <p className="sight-words__subtitle">{t('subtitle', 'High-frequency words for instant recognition')}</p>
            </div>

            <div className="sight-words__intro">
                <p>{t('intro')}</p>
            </div>

            {/* Mastery Legend */}
            {hasMastery && (
                <div className="sight-words__legend">
                    <p className="sight-words__legend-label">{t('mastery.legend', 'Colors show your mastery from Dictation practice')}</p>
                    <div className="sight-words__legend-items">
                        <span className="sight-words__legend-item">
                            <span className="sight-words__legend-dot sight-words__legend-dot--mastered" />
                            {t('mastery.mastered', 'Mastered')}
                        </span>
                        <span className="sight-words__legend-item">
                            <span className="sight-words__legend-dot sight-words__legend-dot--developing" />
                            {t('mastery.developing', 'Developing')}
                        </span>
                        <span className="sight-words__legend-item">
                            <span className="sight-words__legend-dot sight-words__legend-dot--struggling" />
                            {t('mastery.struggling', 'Needs Practice')}
                        </span>
                        <span className="sight-words__legend-item">
                            <span className="sight-words__legend-dot sight-words__legend-dot--unseen" />
                            {t('mastery.unseen', 'Not Yet Seen')}
                        </span>
                    </div>
                </div>
            )}

            {/* TOC */}
            <nav className="sight-words__toc">
                <h2 className="sight-words__toc-title">{t('toc', 'Levels')}</h2>
                <div className="sight-words__toc-levels">
                    {SIGHT_WORD_LEVELS.map(({ level }) => {
                        const stats = levelStats[level - 1];
                        return (
                            <a key={level} href={`#level-${level}`} className={`sight-words__toc-level sight-words__toc-level--${level}`}>
                                <span className="sight-words__toc-level-num">{level}</span>
                                <span className="sight-words__toc-level-name">{t(`level.${level}.name`)}</span>
                                <span className="sight-words__toc-level-count">
                                    {SIGHT_WORD_LEVELS[level - 1].words.length} {t('words', 'words')}
                                </span>
                                {hasMastery && stats.mastered > 0 && (
                                    <span className="sight-words__toc-mastery">
                                        {t('mastery.count', '{{count}} / {{total}} mastered', {
                                            count: stats.mastered,
                                            total: stats.total,
                                        })}
                                    </span>
                                )}
                            </a>
                        );
                    })}
                </div>
            </nav>

            {/* Level sections */}
            {SIGHT_WORD_LEVELS.map(({ level, words }) => (
                <div key={level} id={`level-${level}`} className="sight-words__level-section">
                    <div className="sight-words__level-header">
                        <span className={`sight-words__level-badge sight-words__level-badge--${level}`}>
                            {t(`level.${level}.name`)}
                        </span>
                        <span className="sight-words__level-count">{words.length} {t('words', 'words')}</span>
                        {hasMastery && (
                            <span className="sight-words__level-mastery">
                                {t('mastery.count', '{{count}} / {{total}} mastered', {
                                    count: levelStats[level - 1].mastered,
                                    total: levelStats[level - 1].total,
                                })}
                            </span>
                        )}
                    </div>
                    <p className="sight-words__level-desc">{t(`level.${level}.desc`)}</p>

                    <div className="sight-words__grid">
                        {words.map(word => (
                            <WordCard
                                key={word}
                                word={word}
                                status={getMasteryStatus(masteryMap[word.toLowerCase()])}
                                speakingWord={speakingWord}
                                loadingWord={loadingWord}
                                onSpeak={handleSpeak}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default SightWordsReferencePage;
